import { API_BASE_URL } from '@synergy/config';
import { DownloadIcon } from '@synergy/theme-next';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { IconButton, Tooltip } from '@mui/material';
import { PaloColumn, PaloListView, PaloStatusPill } from '@synergy/components-palo';
import { ReactElement, useEffect } from 'react';
import { getBonusSettlementTranslationId, getBonusSettlementTypeTranslationId } from '@synergy/models';
import { useNavigate } from 'react-router';
import urlJoin from 'url-join';

import { BonusSettlement } from '../../models/bonus-settlement';
import { ROUTES } from '../../../sidebar-menu/navigation-drawer/routes';
import {
    bonusSettlementsRequestingSelector,
    bonusSettlementsSelector,
} from '../store/bonus-settlements-overview-selector';
import { fetchBonusSettlements, resetBonusSettlementsState } from '../store/bonus-settlements-overview-actions';
import { useSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

export const PaloBonusSettlementOverview = (): ReactElement => {
    const dispatch = useThunkDispatch();
    const navigate = useNavigate();
    const bonusSettlements = useSelector(bonusSettlementsSelector);
    const requesting = useSelector(bonusSettlementsRequestingSelector);

    useEffect(() => {
        dispatch(fetchBonusSettlements());

        return () => {
            dispatch(resetBonusSettlementsState());
        };
    }, [dispatch]);

    const columns: PaloColumn<BonusSettlement>[] = [
        {
            key: 'status',
            type: 'status',
            head: <FormattedMessage id="app.table.column.status" />,
            cell: settlement => (
                <PaloStatusPill
                    status={settlement.status}
                    label={<FormattedMessage id={getBonusSettlementTranslationId(settlement.status)} />}
                />
            ),
        },
        {
            key: 'type',
            type: 'classification',
            head: <FormattedMessage id="app.common.type" />,
            cell: settlement => <FormattedMessage id={getBonusSettlementTypeTranslationId(settlement.type)} />,
        },
        {
            key: 'year',
            type: 'count',
            head: <FormattedMessage id="app.common.year" />,
            cell: settlement => settlement.year,
        },
        {
            key: 'billingPeriod',
            type: 'name',
            head: <FormattedMessage id="app.table.column.billingPeriod" />,
            cell: settlement => (
                <>
                    <FormattedDate
                        value={new Date(`${settlement.year}-01-01`)}
                        year="numeric"
                        month="short"
                        day="numeric"
                    />
                    {' – '}
                    <FormattedDate
                        value={new Date(`${settlement.year}-12-31`)}
                        year="numeric"
                        month="short"
                        day="numeric"
                    />
                </>
            ),
        },
        {
            key: 'download',
            type: 'icon',
            head: '',
            cell: settlement => (
                <Tooltip title={<FormattedMessage id="app.bonusSettlements.table.download" />} placement="top">
                    <IconButton
                        size="small"
                        onClick={event => {
                            // The row opens the settlement; this only downloads.
                            event.stopPropagation();
                            window.open(urlJoin(API_BASE_URL, `/bonus-settlements/${settlement.id}/export`), '_self');
                        }}
                    >
                        <DownloadIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                </Tooltip>
            ),
        },
    ];

    return (
        <PaloListView<BonusSettlement>
            title={<FormattedMessage id="app.navigation.bonusSettlements.overview" />}
            columns={columns}
            items={bonusSettlements.items}
            getRowId={settlement => settlement.id}
            requesting={requesting}
            emptyLabel={<FormattedMessage id="app.common.emptyResult" />}
            onRowClick={settlement =>
                navigate(ROUTES.bonusSettlements.detail.base.replace(':id', settlement.id.toString()))
            }
        />
    );
};
